import { Component, ElementRef, Input, OnDestroy, Renderer2, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { SlideHotspotsClickspotsDTO } from '../../../../common/models/slide-hotspots-clickspot.model';
import { PaddingPosition } from '../../../../common/directives';
import { BorderConfig, HorizontalPositionOption, MediaTypeOption, VerticalPositionOption } from '../../../../common/models';
import { VideoComponent } from '../../../../common/components';


@Component({
    selector: 'lib-hotspot-content',
    templateUrl: './hotspot-content.component.html',
    styleUrls: ['./hotspot-content.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HotspotContentComponent implements OnDestroy {

  @ViewChild('clickspotContainer') clickspotContainer?: ElementRef<HTMLElement>;

  @Input() hotspot!: SlideHotspotsClickspotsDTO;
  @Input() index!: number;
  @Input() id?: string;
  @Input() langcode?: string;
  @Input() isTranslation?: boolean;
  @Input() spacing?: PaddingPosition;

  @Input() border: BorderConfig;
  @Input() removeDropShadows = false;

  public isOpen: boolean = false;
  public mediaTypes: typeof MediaTypeOption = MediaTypeOption;

  private resizeObserver: ResizeObserver;

  constructor(
    private readonly renderer: Renderer2
  ) {}

  public playHotspotVideo(event: Event, video: VideoComponent): void {
    event.stopPropagation();
    video?.plyr?.playing ? video?.pauseVideo() : video?.startVideo();
  }

  public removePositionClasses(): void {
    const containerEl = this.clickspotContainer?.nativeElement;

    this.renderer.removeClass(containerEl, HorizontalPositionOption.LEFT);
    this.renderer.removeClass(containerEl, HorizontalPositionOption.RIGHT);
    this.renderer.removeClass(containerEl, VerticalPositionOption.BOTTOM);
    this.renderer.removeClass(containerEl, VerticalPositionOption.TOP);
    this.renderer.removeClass(containerEl, 'position-initialized');
  }

  public close() {
    this.removePositionClasses();
    this.isOpen = false;
    this.resizeObserver?.disconnect();
  }

  public open(buttonElement: HTMLElement): void {
    const containerEl = this.clickspotContainer?.nativeElement;
    const chapterContainerEl = this.getContainerElement();

    if (!containerEl || !buttonElement || !chapterContainerEl) {
      return;
    }

    this.resizeObserver = new ResizeObserver(entries => {
      const dialogWidth = entries[0].contentRect.width;
      let { x: positionX, y: positionY } = this.hotspot;
      const isPhone = globalThis.matchMedia('(max-width: 768px)').matches;
      const fitClassXPosition = this.getDialogFitXPosition(dialogWidth, chapterContainerEl, buttonElement);
      const fitClassYPosition = this.getDialogFitYPosition(buttonElement);

      this.renderer.addClass(containerEl, fitClassYPosition);
      this.renderer.addClass(containerEl, fitClassXPosition);

      if (isPhone) {
        const { min, max } = this.getMaxSafeXPercent(chapterContainerEl.offsetWidth, dialogWidth);
        positionX = Math.min(Math.max(positionX, min), max);
      } else if (fitClassXPosition === HorizontalPositionOption.LEFT) {
        const { min } = this.getMaxSafeXPercent(chapterContainerEl.offsetWidth, dialogWidth);
        positionX = Math.max(positionX, min);
      }

      containerEl.style.setProperty('--lms-clickspot-position-x', `${positionX * 100}%`)
      containerEl.style.setProperty('--lms-clickspot-position-y', `${positionY * 100}%`)

      const containerFromTop = containerEl.getBoundingClientRect().top;
      const chapterContainerFromTop = chapterContainerEl.getBoundingClientRect().top;
      if (chapterContainerFromTop > containerFromTop) {
        const diffFromTop = chapterContainerFromTop - containerFromTop;
        containerEl.style.setProperty('--lms-clickspot-position-y', `calc(${positionY * 100}% + ${diffFromTop}px)`)
      }

      setTimeout(() => {
        this.renderer.addClass(containerEl, 'position-initialized');
      });
    });

    this.resizeObserver.observe(containerEl);
    this.isOpen = true;
  }

  private getContainerElement(): HTMLElement | undefined {
    const containerClasses = ['slide-builder', 'slide-builder-container', 'slide-preview', 'chapters-page'];

    for (const className of containerClasses) {
      const el = document.getElementsByClassName(className)[0];

      if (el) {
        return el as HTMLElement;
      }
    }
  }

  private getMaxSafeXPercent(parentWidth: number, elementWidth: number): { min: number, max: number } {
    const min = (elementWidth / 2) / parentWidth;
    const max = (parentWidth - elementWidth / 2) / parentWidth;

    return { min, max };
  }

  private getDialogFitYPosition(clickspotElement: HTMLElement): VerticalPositionOption {
    const buttonClientRect = clickspotElement.getBoundingClientRect();

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const isUpperHalf = buttonClientRect.top < viewportHeight / 2;

    return isUpperHalf ? VerticalPositionOption.TOP : VerticalPositionOption.BOTTOM;
  }

  private getDialogFitXPosition(dialogWidth: number, chapterContainerEl: HTMLElement, clickspotElement: HTMLElement): HorizontalPositionOption {
    const chapterContainerElClientRect = chapterContainerEl.getBoundingClientRect();
    const buttonClientRect = clickspotElement.getBoundingClientRect();

    const posFromRight = chapterContainerEl.offsetWidth - (buttonClientRect.right - chapterContainerElClientRect.left) - 20;

    if (dialogWidth <= posFromRight) {
      return HorizontalPositionOption.RIGHT;
    }

    return HorizontalPositionOption.LEFT;
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

}
